import { useState, useEffect } from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Modal,
  TextField,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { ValidationMassTransfer } from './ValidationSchema';
import { useForm } from 'react-hook-form';
import { setMassTransfer } from 'services/finance.service';
import { getSecondaryProperties } from 'services/finance.service';
import { getUSNumberFormat, formatCurrency } from 'helpers/formatData';
import axios from 'axios';

const MassTransferModal = ({ modalOpen, setModalOpen, modalCloseAction }) => {
  const [transferValue, setTransferValue] = useState({});
  const {
    propertyId,
    dividend,
    loanRepayment,
    insurance,
    propertyTax,
    managementFees,
    otherExpenses,
  } = transferValue;
  const [propertiesList, setPropertiesList] = useState({});
  const [modalLoading, setModalLoading] = useState(false);
  //validation function for add bank
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
    watch,
  } = useForm({
    mode: 'onTouched',
    resolver: yupResolver(ValidationMassTransfer),
  });
  let fees = watch([
    'loanRepayment',
    'managementFees',
    'insurance',
    'propertyTax',
    'otherExpenses',
  ]);
  fees = fees.reduce(
    (accumulator, currentValue) => accumulator + Number(currentValue),
    0
  );
  const rent = watch('rentAmount');
  //handler function

  const submitMassTransfer = (val) => {
    setTransferValue({
      ...val,
      dividend: rent - fees,
    });
    setModalOpen('Modal3');
  };
  const showErrorMessage = (errorMessage) => {
    return (
      <div style={{ fontSize: 'small', color: 'red' }}>
        {errorMessage.message}
      </div>
    );
  };
  //API Integration
  const handleMassTransfer = async () => {
    setModalLoading(true);
    try {
      const propertyTypeRes = await setMassTransfer(propertyId, {
        dividend: Number(dividend),
        loanRepayment: Number(loanRepayment),
        insurance: Number(insurance),
        propertyTax: Number(propertyTax),
        managementFees: Number(managementFees),
        otherExpenses: Number(otherExpenses),
      });
      if (!propertyTypeRes.isError) {
        setModalOpen('Modal5');
        modalCloseAction();
      } else {
        toast.error(propertyTypeRes?.message);
        setModalOpen('');
      }
    } catch (error) {
      const { response } = error;
      toast.error(response?.data?.message);
      setModalOpen('');
    }
    setModalLoading(false);
    reset();
  };
  const fetchPropertiesInfo = async (cancelToken) => {
    try {
      const res = await getSecondaryProperties(cancelToken);
      if (!res.isError) {
        setPropertiesList(res);
      } else {
        toast.error(res?.message);
      }
    } catch (error) {
      const { response } = error;
      toast.error(response?.data?.message);
    }
  };
  // API call
  useEffect(() => {
    const source = axios.CancelToken.source();
    modalOpen === 'Modal2' && fetchPropertiesInfo(source.token);
    return () => source.cancel('Component got unmounted');
  }, [modalOpen]);
  // if deductions is greater than rent amount
  if (fees > rent) {
    toast.error('Rent amount must be greater than Deductions', {
      toastId: 'minusError',
    });
  }
  const numberInputInvalidChars = ['-', '+', 'e', 'E'];
  return (
    <MDBox>
      <Modal
        open={modalOpen === 'Modal2'}
        onClose={() => setModalOpen('')}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            minWidth: '500px',
            maxHeight: 'calc(100vh - 100px)',
            overflow: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 3,
          }}
        >
          <form onSubmit={handleSubmit(submitMassTransfer)}>
            <MDTypography id="modal-modal-title" variant="h4" component="h2">
              Make Transfer
            </MDTypography>
            <MDBox mt={3} display="flex" flexDirection="column" gap="16px">
              <FormControl fullWidth>
                <InputLabel id="properties">Select Property</InputLabel>
                <Select
                  name="properties"
                  labelId="properties"
                  id="property"
                  sx={{ height: '44px' }}
                  label="Select Property"
                  defaultValue={watch('propertyId')}
                  MenuProps={{
                    style: {
                      maxHeight: '450px',
                    },
                  }}
                  {...register('propertyId')}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Property" />
                  )}
                >
                  {propertiesList?.data?.map((property) => {
                    return (
                      <MenuItem value={property.id} key={property.id}>
                        {property.name}
                      </MenuItem>
                    );
                  })}
                </Select>
                {errors.propertyId ? showErrorMessage(errors.propertyId) : null}
              </FormControl>
              <MDTypography fontWeight="medium">Rent Details</MDTypography>
              <FormControl>
                <MDInput
                  label="Enter Rent Amount"
                  fullWidth
                  type="number"
                  {...register('rentAmount')}
                  onKeyDown={(e) => {
                    if (numberInputInvalidChars.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    return false;
                  }}
                  onWheel={(e) => {
                    e.target.blur();
                  }}
                />
                {errors.rentAmount && showErrorMessage(errors.rentAmount)}
              </FormControl>
              <MDTypography fontWeight="medium">Deductions</MDTypography>
              <MDBox mt={-1}>
                <MDTypography fontSize="14px">
                  No of Investors:
                  <MDTypography
                    variant="span"
                    ml={1}
                    fontSize="14px"
                    fontWeight="medium"
                  >
                    {Boolean(watch('propertyId'))
                      ? getUSNumberFormat(
                          propertiesList?.data?.find(
                            (val) => val.id === watch('propertyId')
                          )?.noOfInvestors
                        )
                      : 'Please Select Property'}
                  </MDTypography>
                </MDTypography>
                <MDTypography fontSize="14px">
                  Amount to disburse:
                  <MDTypography
                    variant="span"
                    ml={1}
                    fontSize="14px"
                    fontWeight="medium"
                  >
                    {formatCurrency(rent - fees)}
                  </MDTypography>
                </MDTypography>
                <MDTypography fontSize="14px">
                  Source Account:
                  <MDTypography
                    variant="span"
                    ml={1}
                    fontSize="14px"
                    fontWeight="medium"
                  >
                    {Boolean(watch('propertyId'))
                      ? 'xxxxxxxx' +
                        propertiesList?.data?.find(
                          (val) => val.id === watch('propertyId')
                        )?.sourceAccountNumber
                      : 'Please Select Property'}
                  </MDTypography>
                </MDTypography>
              </MDBox>
              <FormControl>
                <MDInput
                  label="Loan Payment"
                  fullWidth
                  type="number"
                  {...register('loanRepayment')}
                  onKeyDown={(e) => {
                    if (numberInputInvalidChars.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    return false;
                  }}
                  onWheel={(e) => {
                    e.target.blur();
                  }}
                />
                {errors.loanRepayment && showErrorMessage(errors.loanRepayment)}
              </FormControl>
              <FormControl>
                <MDInput
                  label="Insurance"
                  fullWidth
                  {...register('insurance')}
                  type="number"
                  onKeyDown={(e) => {
                    if (numberInputInvalidChars.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    return false;
                  }}
                  onWheel={(e) => {
                    e.target.blur();
                  }}
                />
                {errors.insurance && showErrorMessage(errors.insurance)}
              </FormControl>
              <FormControl>
                <MDInput
                  label="Tax"
                  fullWidth
                  {...register('propertyTax')}
                  type="number"
                  onKeyDown={(e) => {
                    if (numberInputInvalidChars.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    return false;
                  }}
                  onWheel={(e) => {
                    e.target.blur();
                  }}
                />
                {errors.propertyTax && showErrorMessage(errors.propertyTax)}
              </FormControl>
              <FormControl>
                <MDInput
                  label="Management Fee"
                  fullWidth
                  {...register('managementFees')}
                  type="number"
                  onKeyDown={(e) => {
                    if (numberInputInvalidChars.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    return false;
                  }}
                  onWheel={(e) => {
                    e.target.blur();
                  }}
                />
                {errors.managementFees &&
                  showErrorMessage(errors.managementFees)}
              </FormControl>
              <FormControl>
                <MDInput
                  label="Other Expenses"
                  fullWidth
                  {...register('otherExpenses')}
                  type="number"
                  onKeyDown={(e) => {
                    if (numberInputInvalidChars.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    return false;
                  }}
                  onWheel={(e) => {
                    e.target.blur();
                  }}
                />
                {errors.otherExpenses && showErrorMessage(errors.otherExpenses)}
              </FormControl>
            </MDBox>
            <MDBox align="right" marginTop="32px">
              <MDButton
                onClick={() => {
                  setModalOpen('');
                  reset();
                }}
                type="button"
              >
                Cancel
              </MDButton>
              <MDButton
                color="info"
                type="submit"
                disabled={!isDirty || !isValid || rent < fees}
              >
                Preview Transfer
              </MDButton>
            </MDBox>
          </form>
        </Box>
      </Modal>
      <Modal
        open={modalOpen === 'Modal3'}
        onClose={() => setModalOpen('')}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            minWidth: '500px',
            maxHeight: 'calc(100vh - 100px)',
            overflow: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 3,
          }}
        >
          <MDTypography id="modal-modal-title" variant="h4" component="h2">
            Make Transfer
          </MDTypography>
          <MDBox mt={3} display="flex" flexDirection="column" gap="16px">
            <MDTypography fontWeight="medium">Selected Property</MDTypography>
            <MDInput
              name="selected-property"
              label="Property Name"
              fullWidth
              value={
                propertiesList?.data?.filter(
                  (val) => Number(val.id) === Number(watch('propertyId'))
                )[0]?.name
              }
              disabled
            />

            <MDTypography fontWeight="medium">Rent Details</MDTypography>
            <MDInput
              name="rent-amount"
              label="Rent Amount"
              fullWidth
              value={formatCurrency(watch('rentAmount'))}
              disabled
            />
            <MDTypography fontWeight="medium">Deductions</MDTypography>
            <MDBox mt={-1}>
              <MDTypography fontSize="14px">
                No of Investors:
                <MDTypography
                  variant="span"
                  ml={1}
                  fontSize="14px"
                  fontWeight="medium"
                >
                  {getUSNumberFormat(
                    propertiesList?.data?.find(
                      (val) => val.id === watch('propertyId')
                    )?.noOfInvestors
                  )}
                </MDTypography>
              </MDTypography>
              <MDTypography fontSize="14px">
                Amount to disburse:
                <MDTypography
                  variant="span"
                  ml={1}
                  fontSize="14px"
                  fontWeight="medium"
                >
                  {formatCurrency(rent - fees)}
                </MDTypography>
              </MDTypography>
              <MDTypography fontSize="14px">
                Source Account:
                <MDTypography
                  variant="span"
                  ml={1}
                  fontSize="14px"
                  fontWeight="medium"
                >
                  {'xxxxxxxx' +
                    propertiesList?.data?.find(
                      (val) => val.id === watch('propertyId')
                    )?.sourceAccountNumber}
                </MDTypography>
              </MDTypography>
            </MDBox>
            <MDBox mt={0}>
              <MDTypography fontSize="14px">
                Loan Payment:
                <MDTypography
                  variant="span"
                  ml={1}
                  fontSize="14px"
                  fontWeight="medium"
                >
                  {formatCurrency(watch('loanRepayment'))}
                </MDTypography>
              </MDTypography>
              <MDTypography fontSize="14px">
                Insurance:
                <MDTypography
                  variant="span"
                  ml={1}
                  fontSize="14px"
                  fontWeight="medium"
                >
                  {formatCurrency(watch('insurance'))}
                </MDTypography>
              </MDTypography>
              <MDTypography fontSize="14px">
                Tax:
                <MDTypography
                  variant="span"
                  ml={1}
                  fontSize="14px"
                  fontWeight="medium"
                >
                  {formatCurrency(watch('propertyTax'))}
                </MDTypography>
              </MDTypography>
              <MDTypography fontSize="14px">
                Management Fee:
                <MDTypography
                  variant="span"
                  ml={1}
                  fontSize="14px"
                  fontWeight="medium"
                >
                  {formatCurrency(watch('managementFees'))}
                </MDTypography>
              </MDTypography>
              <MDTypography fontSize="14px">
                Other Expenses:
                <MDTypography
                  variant="span"
                  ml={1}
                  fontSize="14px"
                  fontWeight="medium"
                >
                  {formatCurrency(watch('otherExpenses'))}
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
          <MDBox align="right" marginTop="32px">
            <MDButton onClick={() => setModalOpen('Modal2')}>Back</MDButton>
            <MDButton color="info" onClick={() => setModalOpen('Modal4')}>
              Transfer
            </MDButton>
          </MDBox>
        </Box>
      </Modal>
      <Modal
        open={modalOpen === 'Modal4'}
        onClose={() => {
          setModalOpen('');
          setModalLoading(false);
          reset();
        }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxHeight: 'calc(100vh - 100px)',
            overflow: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 3,
          }}
        >
          <MDTypography
            id="modal-modal-title"
            align="center"
            variant="h4"
            component="h2"
          >
            Alert
          </MDTypography>
          <MDTypography mt={4}>
            Are you sure you want to initiate transfer?
          </MDTypography>
          <MDBox
            align="right"
            display="flex"
            gap={2}
            justifyContent="flex-end"
            marginTop="32px"
          >
            <MDButton
              color="error"
              onClick={() => {
                setModalOpen('');
                setModalLoading(false);
                reset();
              }}
            >
              No, Cancel
            </MDButton>
            <MDButton
              color="info"
              isLoading={modalLoading}
              onClick={() => {
                handleMassTransfer();
              }}
            >
              Yes, Transfer
            </MDButton>
          </MDBox>
        </Box>
      </Modal>
      <Modal
        open={modalOpen === 'Modal5'}
        onClose={() => setModalOpen('')}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxHeight: 'calc(100vh - 100px)',
            overflow: 'auto',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 3,
          }}
        >
          <MDTypography
            id="modal-modal-title"
            align="center"
            variant="h4"
            component="h2"
          >
            Congratulations
          </MDTypography>
          <Box mt={4} align="center">
            <CheckCircleIcon
              sx={{ width: '32px', height: '32px' }}
              color="success"
            ></CheckCircleIcon>
            <MDTypography>
              Successfully Initiated Dividend Transfer
            </MDTypography>
          </Box>
          <MDBox
            align="right"
            display="flex"
            gap={2}
            justifyContent="center"
            marginTop="32px"
          >
            <MDButton color="info" onClick={() => setModalOpen('')}>
              Done
            </MDButton>
          </MDBox>
        </Box>
      </Modal>
    </MDBox>
  );
};

export default MassTransferModal;
